import React, { useState, useEffect, useCallback } from 'react';
import {
  X, ChevronDown, ChevronRight, Plus, Trash2, Save, AlertCircle,
  CheckCircle, Edit2, BookOpen, Calendar, GripVertical, Loader
} from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CATEGORIES = [
  'Chronological', 'Entire Bible', 'Topical', 'New Testament',
  'Old Testament', 'Wisdom Literature', 'Gospels', 'Epistles', 'Christ'
];

const ReadingPlanEditor = ({ plan, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [loadingDays, setLoadingDays] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Plan metadata
  const [metadata, setMetadata] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    category: plan?.category || 'Chronological',
    estimated_daily_reading_time: plan?.estimated_daily_reading_time || 15,
    is_public: plan?.is_public ?? true,
    is_featured: plan?.is_featured ?? false,
  });

  // Days with their references
  const [days, setDays] = useState([]);
  const [expandedDays, setExpandedDays] = useState({});
  const [editingDay, setEditingDay] = useState(null); // day id being edited inline
  const [editingRef, setEditingRef] = useState(null); // ref id being edited inline

  useEffect(() => {
    loadDaysAndReferences();
  }, [plan?.id]);

  const loadDaysAndReferences = async () => {
    try {
      setLoadingDays(true);
      const { data, error } = await supabase?.from('reading_plan_days')?.select(`*, reading_plan_references(*)`)?.eq('reading_plan_id', plan?.id)?.order('day_number', { ascending: true });

      if (error) throw error;

      const sorted = (data || [])?.map(day => ({
        ...day,
        reading_plan_references: (day?.reading_plan_references || [])?.sort(
          (a, b) => (a?.display_order || 0) - (b?.display_order || 0)
        )
      }));

      setDays(sorted);
      // Expand first day by default
      if (sorted?.length > 0) {
        setExpandedDays({ [sorted?.[0]?.id]: true });
      }
    } catch (err) {
      setError('Failed to load days: ' + err?.message);
    } finally {
      setLoadingDays(false);
    }
  };

  // ── Metadata save ──────────────────────────────────────────────────────────
  const saveMetadata = async () => {
    if (!metadata?.name?.trim()) {
      setError('Plan name is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const { error } = await supabase?.from('reading_plans')?.update({
          name: metadata?.name?.trim(),
          description: metadata?.description?.trim() || null,
          category: metadata?.category,
          estimated_daily_reading_time: metadata?.estimated_daily_reading_time,
          is_public: metadata?.is_public,
          is_featured: metadata?.is_featured,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', plan?.id);

      if (error) throw error;
      setSuccess('Plan details saved!');
      setTimeout(() => setSuccess(''), 3000);
      onSaved?.();
    } catch (err) {
      setError('Failed to save: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Day operations ─────────────────────────────────────────────────────────
  const toggleDay = (dayId) => {
    setExpandedDays(prev => ({ ...prev, [dayId]: !prev?.[dayId] }));
  };

  const startEditDay = (day) => {
    setEditingDay({ id: day?.id, title: day?.title || '', description: day?.description || '' });
  };

  const saveDay = async () => {
    if (!editingDay) return;
    try {
      setSaving(true);
      setError('');
      const { error } = await supabase?.from('reading_plan_days')?.update({
          title: editingDay?.title?.trim() || null,
          description: editingDay?.description?.trim() || null,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', editingDay?.id);

      if (error) throw error;

      setDays(prev => prev?.map(d =>
        d?.id === editingDay?.id
          ? { ...d, title: editingDay?.title, description: editingDay?.description }
          : d
      ));
      setEditingDay(null);
      setSuccess('Day saved!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to save day: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const addDay = async () => {
    const maxDay = days?.length > 0 ? Math.max(...days?.map(d => d?.day_number || 0)) : 0;
    const newDayNumber = maxDay + 1;
    try {
      setSaving(true);
      setError('');
      const { data, error } = await supabase?.from('reading_plan_days')?.insert({
          reading_plan_id: plan?.id,
          day_number: newDayNumber,
          title: `Day ${newDayNumber}`,
          description: '',
          display_order: newDayNumber,
        })?.select()?.single();

      if (error) throw error;

      const newDay = { ...data, reading_plan_references: [] };
      setDays(prev => [...prev, newDay]);
      setExpandedDays(prev => ({ ...prev, [data?.id]: true }));

      // Update plan duration
      await supabase?.from('reading_plans')?.update({ duration_days: newDayNumber, updated_at: new Date()?.toISOString() })?.eq('id', plan?.id);

      onSaved?.();
    } catch (err) {
      setError('Failed to add day: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteDay = async (dayId) => {
    if (!confirm('Delete this day and all its readings?')) return;
    try {
      setSaving(true);
      setError('');
      // Delete references first
      await supabase?.from('reading_plan_references')?.delete()?.eq('reading_plan_day_id', dayId);
      const { error } = await supabase?.from('reading_plan_days')?.delete()?.eq('id', dayId);
      if (error) throw error;

      const remaining = days?.filter(d => d?.id !== dayId);
      setDays(remaining);

      // Update plan duration
      const maxDay = remaining?.length > 0 ? Math.max(...remaining?.map(d => d?.day_number || 0)) : 0;
      await supabase?.from('reading_plans')?.update({ duration_days: maxDay, total_readings: remaining?.reduce((s, d) => s + (d?.reading_plan_references?.length || 0), 0), updated_at: new Date()?.toISOString() })?.eq('id', plan?.id);

      onSaved?.();
    } catch (err) {
      setError('Failed to delete day: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Reference operations ───────────────────────────────────────────────────
  const startEditRef = (ref) => {
    setEditingRef({
      id: ref?.id,
      dayId: ref?.reading_plan_day_id,
      book_name: ref?.book_name || '',
      chapter: ref?.chapter || '',
      verse_start: ref?.verse_start || '',
      verse_end: ref?.verse_end || '',
      reference_text: ref?.reference_text || '',
    });
  };

  const buildReferenceText = (book, chapter, verseStart, verseEnd) => {
    if (!book || !chapter) return '';
    let text = `${book} ${chapter}`;
    if (verseStart) {
      text += `:${verseStart}`;
      if (verseEnd && verseEnd !== verseStart) text += `-${verseEnd}`;
    }
    return text;
  };

  const saveRef = async () => {
    if (!editingRef) return;
    if (!editingRef?.book_name?.trim() || !editingRef?.chapter) {
      setError('Book name and chapter are required');
      return;
    }
    try {
      setSaving(true);
      setError('');

      const refText = buildReferenceText(
        editingRef?.book_name,
        editingRef?.chapter,
        editingRef?.verse_start,
        editingRef?.verse_end
      );

      const updateData = {
        book_name: editingRef?.book_name?.trim(),
        chapter: parseInt(editingRef?.chapter),
        verse_start: editingRef?.verse_start ? parseInt(editingRef?.verse_start) : null,
        verse_end: editingRef?.verse_end ? parseInt(editingRef?.verse_end) : null,
        reference_text: refText,
      };

      const { error } = await supabase?.from('reading_plan_references')?.update(updateData)?.eq('id', editingRef?.id);

      if (error) throw error;

      setDays(prev => prev?.map(d =>
        d?.id === editingRef?.dayId
          ? {
              ...d,
              reading_plan_references: d?.reading_plan_references?.map(r =>
                r?.id === editingRef?.id ? { ...r, ...updateData } : r
              )
            }
          : d
      ));
      setEditingRef(null);
      setSuccess('Reference saved!');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError('Failed to save reference: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const addReference = async (dayId) => {
    const day = days?.find(d => d?.id === dayId);
    const nextOrder = (day?.reading_plan_references?.length || 0) + 1;
    try {
      setSaving(true);
      setError('');
      const { data, error } = await supabase?.from('reading_plan_references')?.insert({
          reading_plan_day_id: dayId,
          book_name: 'Genesis',
          chapter: 1,
          verse_start: null,
          verse_end: null,
          reference_text: 'Genesis 1',
          display_order: nextOrder,
        })?.select()?.single();

      if (error) throw error;

      setDays(prev => prev?.map(d =>
        d?.id === dayId
          ? { ...d, reading_plan_references: [...d?.reading_plan_references, data] }
          : d
      ));

      // Open edit immediately
      setEditingRef({
        id: data?.id,
        dayId,
        book_name: 'Genesis',
        chapter: 1,
        verse_start: '',
        verse_end: '',
        reference_text: 'Genesis 1',
      });

      // Update total_readings count
      const totalRefs = days?.reduce((s, d) => s + (d?.reading_plan_references?.length || 0), 0) + 1;
      await supabase?.from('reading_plans')?.update({ total_readings: totalRefs, updated_at: new Date()?.toISOString() })?.eq('id', plan?.id);

      onSaved?.();
    } catch (err) {
      setError('Failed to add reference: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteReference = async (refId, dayId) => {
    if (!confirm('Remove this reading reference?')) return;
    try {
      setSaving(true);
      setError('');
      const { error } = await supabase?.from('reading_plan_references')?.delete()?.eq('id', refId);
      if (error) throw error;

      setDays(prev => prev?.map(d =>
        d?.id === dayId
          ? { ...d, reading_plan_references: d?.reading_plan_references?.filter(r => r?.id !== refId) }
          : d
      ));

      const totalRefs = days?.reduce((s, d) => {
        if (d?.id === dayId) return s + d?.reading_plan_references?.filter(r => r?.id !== refId)?.length;
        return s + (d?.reading_plan_references?.length || 0);
      }, 0);
      await supabase?.from('reading_plans')?.update({ total_readings: totalRefs, updated_at: new Date()?.toISOString() })?.eq('id', plan?.id);

      onSaved?.();
    } catch (err) {
      setError('Failed to delete reference: ' + err?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-4xl my-4 border border-border">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between rounded-t-lg z-10">
          <div>
            <h2 className="text-lg font-bold text-foreground">Edit Reading Plan</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{plan?.name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/50">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Alerts */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <span className="text-sm text-destructive">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-destructive"><X className="w-3.5 h-3.5" /></button>
            </div>
          )}
          {success && (
            <div className="bg-success/10 border border-success/30 rounded-lg p-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-success" />
              <span className="text-sm text-success">{success}</span>
            </div>
          )}

          {/* ── Plan Metadata ── */}
          <section className="bg-muted/20 rounded-lg border border-border p-4 space-y-4">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              Plan Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Input
                  label="Plan Name"
                  value={metadata?.name}
                  onChange={e => setMetadata(p => ({ ...p, name: e?.target?.value }))}
                  placeholder="e.g., Bible in a Year"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-1.5">Description</label>
                <textarea
                  value={metadata?.description}
                  onChange={e => setMetadata(p => ({ ...p, description: e?.target?.value }))}
                  rows={2}
                  className="w-full rounded-md border border-border px-3 py-2 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Describe the reading plan..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
                <select
                  value={metadata?.category}
                  onChange={e => setMetadata(p => ({ ...p, category: e?.target?.value }))}
                  className="w-full rounded-md border border-border px-3 py-2 text-sm bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  {CATEGORIES?.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Input
                  label="Daily Reading Time (min)"
                  type="number"
                  min={1}
                  value={metadata?.estimated_daily_reading_time}
                  onChange={e => setMetadata(p => ({ ...p, estimated_daily_reading_time: parseInt(e?.target?.value) || 15 }))}
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={metadata?.is_public}
                    onChange={e => setMetadata(p => ({ ...p, is_public: e?.target?.checked }))}
                    className="rounded border-border text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Public</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={metadata?.is_featured}
                    onChange={e => setMetadata(p => ({ ...p, is_featured: e?.target?.checked }))}
                    className="rounded border-border text-primary focus:ring-ring"
                  />
                  <span className="text-sm text-foreground">Featured</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end">
              <Button size="sm" onClick={saveMetadata} loading={saving}>
                <Save className="w-3.5 h-3.5 mr-1.5" />
                Save Details
              </Button>
            </div>
          </section>

          {/* ── Days & References ── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Calendar className="w-4 h-4 text-primary" />
                Days &amp; Readings
                <span className="text-xs font-normal text-muted-foreground">({days?.length} days)</span>
              </h3>
              <Button size="sm" variant="outline" onClick={addDay} disabled={saving}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Day
              </Button>
            </div>

            {loadingDays ? (
              <div className="flex items-center justify-center py-10">
                <Loader className="w-6 h-6 animate-spin text-primary" />
                <span className="ml-2 text-sm text-muted-foreground">Loading days...</span>
              </div>
            ) : days?.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border rounded-lg">
                <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No days yet. Add your first day.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {days?.map((day) => (
                  <div key={day?.id} className="border border-border rounded-lg overflow-hidden">
                    {/* Day header row */}
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors">
                      <button
                        onClick={() => toggleDay(day?.id)}
                        className="flex items-center gap-2 flex-1 text-left min-w-0"
                      >
                        {expandedDays?.[day?.id]
                          ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                        }
                        <span className="text-xs font-semibold text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                          Day {day?.day_number}
                        </span>

                        {editingDay?.id === day?.id ? (
                          <input
                            autoFocus
                            value={editingDay?.title}
                            onChange={e => setEditingDay(p => ({ ...p, title: e?.target?.value }))}
                            onClick={e => e?.stopPropagation()}
                            className="flex-1 min-w-0 text-sm bg-input border border-border rounded px-2 py-0.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            placeholder="Day title..."
                          />
                        ) : (
                          <span className="text-sm font-medium text-foreground truncate">
                            {day?.title || `Day ${day?.day_number}`}
                          </span>
                        )}

                        <span className="text-xs text-muted-foreground shrink-0 ml-auto mr-2">
                          {day?.reading_plan_references?.length || 0} reading{day?.reading_plan_references?.length !== 1 ? 's' : ''}
                        </span>
                      </button>

                      {/* Day actions */}
                      <div className="flex items-center gap-1 shrink-0">
                        {editingDay?.id === day?.id ? (
                          <>
                            <button
                              onClick={e => { e?.stopPropagation(); saveDay(); }}
                              className="text-success hover:text-success/80 p-1 rounded hover:bg-success/10 transition-colors"
                              title="Save"
                            >
                              <Save className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={e => { e?.stopPropagation(); setEditingDay(null); }}
                              className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted/50 transition-colors"
                              title="Cancel"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={e => { e?.stopPropagation(); startEditDay(day); }}
                            className="text-muted-foreground hover:text-primary p-1 rounded hover:bg-primary/10 transition-colors"
                            title="Edit day title"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={e => { e?.stopPropagation(); deleteDay(day?.id); }}
                          className="text-muted-foreground hover:text-destructive p-1 rounded hover:bg-destructive/10 transition-colors"
                          title="Delete day"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Day description (when editing) */}
                    {editingDay?.id === day?.id && (
                      <div className="px-3 py-2 bg-muted/10 border-t border-border">
                        <textarea
                          value={editingDay?.description}
                          onChange={e => setEditingDay(p => ({ ...p, description: e?.target?.value }))}
                          rows={2}
                          className="w-full text-xs bg-input border border-border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                          placeholder="Day description (optional)..."
                        />
                      </div>
                    )}

                    {/* References list */}
                    {expandedDays?.[day?.id] && (
                      <div className="divide-y divide-border/50">
                        {day?.reading_plan_references?.length === 0 ? (
                          <div className="px-4 py-3 text-xs text-muted-foreground italic">
                            No readings assigned to this day.
                          </div>
                        ) : (
                          day?.reading_plan_references?.map((ref) => (
                            <div key={ref?.id} className="px-4 py-2">
                              {editingRef?.id === ref?.id ? (
                                /* Inline reference editor */
                                (<div className="space-y-2">
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <div className="sm:col-span-2">
                                      <label className="block text-xs font-medium text-muted-foreground mb-1">Book</label>
                                      <input
                                        value={editingRef?.book_name}
                                        onChange={e => setEditingRef(p => ({
                                          ...p,
                                          book_name: e?.target?.value,
                                          reference_text: buildReferenceText(e?.target?.value, p?.chapter, p?.verse_start, p?.verse_end)
                                        }))}
                                        className="w-full text-sm bg-input border border-border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                        placeholder="e.g., Genesis"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-muted-foreground mb-1">Chapter</label>
                                      <input
                                        type="number"
                                        min={1}
                                        value={editingRef?.chapter}
                                        onChange={e => setEditingRef(p => ({
                                          ...p,
                                          chapter: e?.target?.value,
                                          reference_text: buildReferenceText(p?.book_name, e?.target?.value, p?.verse_start, p?.verse_end)
                                        }))}
                                        className="w-full text-sm bg-input border border-border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-1">
                                      <div>
                                        <label className="block text-xs font-medium text-muted-foreground mb-1">V. Start</label>
                                        <input
                                          type="number"
                                          min={1}
                                          value={editingRef?.verse_start}
                                          onChange={e => setEditingRef(p => ({
                                            ...p,
                                            verse_start: e?.target?.value,
                                            reference_text: buildReferenceText(p?.book_name, p?.chapter, e?.target?.value, p?.verse_end)
                                          }))}
                                          className="w-full text-sm bg-input border border-border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                          placeholder="—"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs font-medium text-muted-foreground mb-1">V. End</label>
                                        <input
                                          type="number"
                                          min={1}
                                          value={editingRef?.verse_end}
                                          onChange={e => setEditingRef(p => ({
                                            ...p,
                                            verse_end: e?.target?.value,
                                            reference_text: buildReferenceText(p?.book_name, p?.chapter, p?.verse_start, e?.target?.value)
                                          }))}
                                          className="w-full text-sm bg-input border border-border rounded px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                                          placeholder="—"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  {editingRef?.reference_text && (
                                    <p className="text-xs text-muted-foreground">
                                      Preview: <span className="font-medium text-foreground">{editingRef?.reference_text}</span>
                                    </p>
                                  )}
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={saveRef}
                                      disabled={saving}
                                      className="inline-flex items-center gap-1 text-xs bg-primary text-primary-foreground px-2.5 py-1 rounded hover:bg-primary/90 transition-colors disabled:opacity-50"
                                    >
                                      <Save className="w-3 h-3" />
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setEditingRef(null)}
                                      className="text-xs text-muted-foreground hover:text-foreground px-2.5 py-1 rounded border border-border hover:bg-muted/50 transition-colors"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>)
                              ) : (
                                /* Reference display row */
                                (<div className="flex items-center gap-2 group">
                                  <GripVertical className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                                  <span className="text-sm text-foreground flex-1">
                                    {ref?.reference_text || `${ref?.book_name} ${ref?.chapter}`}
                                  </span>
                                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button
                                      onClick={() => startEditRef(ref)}
                                      className="text-muted-foreground hover:text-primary p-1 rounded hover:bg-primary/10 transition-colors"
                                      title="Edit reference"
                                    >
                                      <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => deleteReference(ref?.id, day?.id)}
                                      className="text-muted-foreground hover:text-destructive p-1 rounded hover:bg-destructive/10 transition-colors"
                                      title="Remove reference"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </div>)
                              )}
                            </div>
                          ))
                        )}

                        {/* Add reference button */}
                        <div className="px-4 py-2">
                          <button
                            onClick={() => addReference(day?.id)}
                            disabled={saving}
                            className="inline-flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-3.5 h-3.5" />
                            Add Reading
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-muted/30 px-6 py-3 border-t border-border flex justify-end rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReadingPlanEditor;
